<template>
  <transition name="modal-fade">
    <div v-if="visible" class="modal-overlay" @click.self="cancel">
      <!-- 整窗（卡片）就是这一个整体：书签行在卡片内部顶端，与下面内容连成一体，不再单独凸到窗外
           （类型配色变量 --bm-tab-bg / --bm-accent 挂在卡片上） -->
      <div class="modal-card" :class="cardKindClass">
        <!-- 书签行：左侧标题 + 记录/在做/计划 三个切换 + 右侧关闭，一行放完 -->
        <div class="bookmark-row">
          <span class="bookmark bm-title">{{ eventData ? '修改记录' : '添加记录' }}</span>
          <div class="bookmark-group">
            <button
              type="button"
              class="bookmark bm--record"
              :class="{ on: blockKind === 'done' }"
              @click="blockKind = 'done'"
            >记录</button>
            <button
              type="button"
              class="bookmark bm--doing"
              :class="{ on: blockKind === 'running', 'is-off': doingDisabled }"
              :title="doingDisabled ? '「在做」只能从今天开始' : ''"
              :disabled="doingDisabled"
              @click="blockKind = 'running'"
            >在做</button>
            <button
              type="button"
              class="bookmark bm--plan"
              :class="{ on: blockKind === 'planned' }"
              @click="blockKind = 'planned'"
            >计划</button>
            <!-- 关闭按钮同行靠右 -->
            <button
              type="button"
              class="bookmark bm--close"
              title="关闭"
              @click="cancel"
            >×</button>
          </div>
        </div>

        <!-- 子页面：整窗纯白、不再随类型变色；记录填完整时间、在做无结束时间、计划暂与记录一致 -->
        <div class="sub-page">
          <div class="sub-page-head">
            <span class="sub-page-title">{{ subPageTitle }}</span>
            <span class="sub-page-desc">{{ subPageDesc }}</span>
          </div>

          <div class="form-item">
            <label>分类</label>
            <div v-if="loadingCats" class="cat-loading">加载分类中…</div>
            <div v-else-if="catOptions.length === 0" class="cat-empty">暂无分类，请先在上方“自定义”创建一个</div>
            <div v-else class="cat-buttons">
              <!-- 「添加记录」里的分类与「我的分类」图例共用 CategoryChip：
                   - 同一份外观、同一份配色（取自 constants.categoryPalette），永远不再分头改造
                   - `:selected` 由 CategoryChip 内部把"底色=边框色=分类原色"，
                     即用户要求「选中时底色变成边框颜色」 -->
              <CategoryChip
                v-for="cat in catOptions"
                :key="cat.key"
                :color="cat.color"
                :label="cat.label"
                :selected="selectedCat === cat.key"
                editable
                @click="selectedCat = cat.key"
              />
            </div>
          </div>

        <div class="form-item">
          <label>
            时间
            <span class="label-note">{{ timeHint }}</span>
            <!-- 滑块 / 选择器 二选一开关：只有「记录 / 计划」需要（在做没有时间轴了） -->
            <div
              v-if="showEnd"
              class="custom-switch"
              :class="{ blue: mode === 'picker' }"
              @click="toggleMode"
            >
              <div class="switch-track">
                <div
                  class="switch-thumb"
                  :class="{ right: mode === 'picker' }"
                />
              </div>
            </div>
          </label>
          <!-- ===== 在做：不要时间轴，只有一个「开始时间」选择器（默认当前时间） ===== -->
          <div v-if="!showEnd" class="time-row">
            <el-time-picker
              v-model="startTimeStr"
              popper-class="quick-add-time-picker"
              placeholder="开始时间"
              format="HH:mm"
              value-format="HH:mm"
              :picker-options="timePickerOptions"
              :clearable="false"
            />
          </div>
          <template v-else>
          <div
            v-if="mode === 'slider'"
            class="timeline-slider"
            :style="{ '--slider-color': selectedCatColor }"
          >
            <div class="slider-labels-top">
              <!-- 开始时间标签：左右各一个 −/+ 直接微调开始时间。
                   hover 数字显示按钮、离开数字延迟隐藏、离开按钮立即隐藏（enterTune/leaveTune 控制） -->
              <div class="thumb-label" :style="startLabelStyle" :class="{ 'show-tune': tuneShow.start }">
                <button
                  type="button"
                  class="lbl-btn"
                  title="-1 分钟"
                  @click="bumpStart(-1)"
                  @mouseenter="enterTune('start')"
                  @mouseleave="leaveTune('start','btn')"
                  :disabled="selectionStart <= sliderMin"
                >−</button>
                <span
                  class="lbl-text"
                  @mouseenter="enterTune('start')"
                  @mouseleave="leaveTune('start','text')"
                >{{ startDateTime }}</span>
                <button
                  type="button"
                  class="lbl-btn"
                  title="+1 分钟"
                  @click="bumpStart(1)"
                  @mouseenter="enterTune('start')"
                  @mouseleave="leaveTune('start','btn')"
                  :disabled="selectionStart >= selectionEnd"
                >+</button>
              </div>
              <!-- 结束时间标签：左右各一个 −/+ 直接微调结束时间 -->
              <div v-show="showEnd" class="thumb-label" :style="endLabelStyle" :class="{ 'show-tune': tuneShow.end }">
                <button
                  type="button"
                  class="lbl-btn"
                  title="-1 分钟"
                  @click="bumpEnd(-1)"
                  @mouseenter="enterTune('end')"
                  @mouseleave="leaveTune('end','btn')"
                  :disabled="selectionEnd <= selectionStart"
                >−</button>
                <span
                  class="lbl-text"
                  @mouseenter="enterTune('end')"
                  @mouseleave="leaveTune('end','text')"
                >{{ endDateTime }}</span>
                <button
                  type="button"
                  class="lbl-btn"
                  title="+1 分钟"
                  @click="bumpEnd(1)"
                  @mouseenter="enterTune('end')"
                  @mouseleave="leaveTune('end','btn')"
                  :disabled="selectionEnd >= sliderMax"
                >+</button>
              </div>
            </div>
            <el-slider
              v-model="sliderRange"
              :min="sliderMin"
              :max="sliderMax"
              :step="1"
              range
              :marks="timelineMarks"
              :format-tooltip="formatTooltip"
              :show-tooltip="!fullscreen"
            />
            <div class="slider-marks-spacer"></div>
          </div>
          <div v-else class="time-row">
            <el-time-picker
              v-model="startTimeStr"
              popper-class="quick-add-time-picker"
              placeholder="开始时间"
              format="HH:mm"
              value-format="HH:mm"
              :picker-options="timePickerOptions"
              :clearable="false"
            />
            <el-time-picker
              v-model="endTimeStr"
              popper-class="quick-add-time-picker"
              placeholder="结束时间"
              format="HH:mm"
              value-format="HH:mm"
              :picker-options="timePickerOptions"
              :clearable="false"
            />
          </div>
          </template>
        </div>

        <!-- ===== 定时提醒：只有「在做」才有。打开后选一个时刻，到点提醒：继续（可再定时）或结束 ===== -->
        <div v-if="!showEnd" class="form-item">
          <label>
            定时
            <span class="label-note">（到点提醒你：继续还是结束）</span>
            <div
              class="custom-switch timer-switch"
              :class="{ on: timerEnabled }"
              @click="toggleTimer"
            >
              <div class="switch-track">
                <div
                  class="switch-thumb"
                  :class="{ right: timerEnabled }"
                />
              </div>
            </div>
          </label>
          <div v-if="timerEnabled" class="timer-body">
            <div class="time-row">
              <el-time-picker
                v-model="timerTimeStr"
                popper-class="quick-add-time-picker"
                placeholder="提醒时间"
                format="HH:mm"
                value-format="HH:mm"
                :picker-options="timerPickerOptions"
                :clearable="false"
                @change="timerQuickActive = null"
              />
            </div>
            <div class="timer-quick">
              <button
                v-for="m in timerQuickOptions"
                :key="m"
                type="button"
                class="timer-quick-btn"
                :class="{ on: timerQuickActive === m }"
                @click="applyQuickTimer(m)"
              >+{{ m }} 分钟</button>
            </div>
          </div>
        </div>

        <div class="form-item">
          <label>事项</label>
          <el-input
            v-model="text"
            type="textarea"
            :rows="4"
            placeholder="请输入事项"
          />
        </div>

        </div>

        <div class="footer">
          <!-- 编辑已有记录时，「取消」按钮变成「删除」按钮，方便直接从弹窗删除该时间块（× / 点击遮罩仍可取消） -->
          <el-button v-if="eventData" type="danger" plain @click="remove">删除</el-button>
          <el-button v-else @click="cancel">取消</el-button>
          <el-button type="primary" :loading="saving" :disabled="saving" @click="confirm">保存</el-button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { DAY_MINUTES } from './constants'
import CategoryChip from './CategoryChip.vue'
import { listTagsByProject } from '@/api/tag.js'
import { createTimeBlock, updateTimeBlock } from '@/api/timeline.js'
import { listFriendTags } from '@/api/share.js'
import { ensureNotifyPermission, timerOf } from '@/utils/runningTimer'

export default {
  name: 'QuickAddModal',
  components: { CategoryChip },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    dayIdx: {
      type: Number,
      default: 0
    },
    rangeStart: {
      type: Number,
      default: 0
    },
    rangeEnd: {
      type: Number,
      default: DAY_MINUTES
    },
    lastCategory: {
      type: String,
      default: ''
    },
    eventData: {
      type: Object,
      default: null
    },
    days: {
      type: Array,
      default: () => []
    },
    projectId: {
      type: [String, Number],
      default: null
    },
    /**
     * 好友协同编辑场景：非 null 时本弹窗是在「被授权编辑的好友」的身份下，
     * 给好友（owner）的工作表新增 / 修改时间块。
     * 此时：
     * - 分类走好友接口 `listFriendTags(ownerId, projectId)`，而不是自己的 `listTagsByProject`；
     * - 落库**不**在这里调自己的时间轴接口（调了会因无权限报错、且会和父组件的好友接口重复），
     *   直接 emit 出去，由父组件（TimeTable → FriendTablePanel）走好友接口。
     * 由 TimeTable 的 friendOwnerId 透传下来；所有者自己的表为 null。
     */
    friendOwnerId: {
      type: [String, Number],
      default: null
    },
    /**
     * 宿主时间表是否处于全屏视图。全屏时 el-slider 的拖动 tooltip 会不可见：
     * el-tooltip 的浮层由 vue-popper 挂到 document.body，会被全屏中的
     * .tablebox（浏览器 top layer）盖住 —— 值关掉 tooltip，
     * 时间数值由滑块上方常驻的 thumb-label 展示，不丢信息。
     */
    fullscreen: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedCat: '',
      mode: 'slider',
      // 任务类型：done（记录）/ running（在做）/ planned（计划）
      blockKind: 'done',
      dragStartMinute: 0,
      dragEndMinute: 0,
      text: '',
      catOptions: [],
      loadingCats: false,
      saving: false,
      // ===== 定时提醒（仅「在做」）=====
      timerEnabled: false,
      /** 提醒时刻，'HH:mm'；toggleTimer 打开时按「开始 + 30 分钟」预填 */
      timerTimeStr: '',
      /** 当前选中的快捷时长（分钟），手动改了时间选择器就置空 */
      timerQuickActive: null,
      timerQuickOptions: [10, 25, 45, 60],
      // 时间轴微调按钮的悬停显隐：hover 数字显示、离开数字延迟隐藏、离开按钮立即隐藏
      // （纯 CSS 无法区分「离开数字」与「离开按钮」两种来源，故用 JS 控制）
      tuneShow: { start: false, end: false },
      tuneTimer: { start: null, end: null }
    }
  },
  computed: {
    DAY_MINUTES() {
      return DAY_MINUTES
    },

    selectionStart() {
      return Math.min(this.dragStartMinute, this.dragEndMinute)
    },
    selectionEnd() {
      return Math.max(this.dragStartMinute, this.dragEndMinute)
    },
    sliderRange: {
      get() {
        return [this.selectionStart, this.selectionEnd]
      },
      set(val) {
        this.dragStartMinute = val[0]
        this.dragEndMinute = val[1]
      }
    },
    startDateTime() {
      return this.buildTime(this.selectionStart)
    },
    endDateTime() {
      return this.buildTime(this.selectionEnd)
    },
    /**
     * 开始时间的可选上界。
     * 记录 / 计划：可以一路选到空档末尾；在做：必须留至少 1 分钟给结束点
     * （结束点在界面上不暴露，但要保证块有长度）。
     * `timePickerOptions` 的右边界直接取这里，保证「面板可选 = setter 许可」。
     */
    startMax() {
      const bound = Math.min(this.rangeEnd, DAY_MINUTES)
      return this.showEnd ? bound : Math.max(this.sliderMin, bound - 1)
    },
    /**
     * ⚠️ 2026-10-13 修「选中的时间和滚轴不一致」：Element 的时间面板会把超出
     * selectableRange 的值**静默夹回下界**（panel.date 被夹住、输入框仍显示原始值），
     * 症状就是「输入框一个时间、下面滚轴高亮另一个时间」。
     * 另一个同源问题：selectionStart = min(dragStart, dragEnd)，写入的开始时间一旦晚于
     * 结束时间，取值被 min 吃掉、输入框回弹成旧值，用户看到的就是「选了没生效」。
     * 所以 setter 必须同时保证：① 夹在 [sliderMin, startMax]；② dragStart ≤ dragEnd。
     */
    startTimeStr: {
      get() {
        return this.startDateTime
      },
      set(val) {
        const m = this.timeToMinutes(val)
        const next = Math.min(Math.max(m, this.sliderMin), this.startMax)
        this.dragStartMinute = next
        if (this.dragEndMinute < next) this.dragEndMinute = next
      }
    },
    endTimeStr: {
      get() {
        return this.endDateTime
      },
      set(val) {
        const m = this.timeToMinutes(val)
        const next = Math.min(Math.max(m, this.dragStartMinute), this.sliderMax)
        this.dragEndMinute = next
        if (this.dragStartMinute > next) this.dragStartMinute = next
      }
    },
    /**
     * 「时间」选择器的可选范围。
     * ⚠️ 右边界必须和 startMax() 完全一致：否则用户能在面板里选到一个值、setter 又把它夹回来，
     *    输入框和滚轴立刻又不一致（Element 只静默夹 panel.date，输入框仍显示未夹的原始值）。
     *    所以这里直接复用 startMax，别各算一套。
     */
    timePickerOptions() {
      const startStr = this.buildTime(this.sliderMin) + ':00'
      const endStr = this.startMax >= DAY_MINUTES
        ? '23:59:59'
        : this.buildTime(this.startMax) + ':59'
      return {
        selectableRange: `${startStr} - ${endStr}`
      }
    },
    selectedCatColor() {
      const cat = this.catOptions.find(c => c.key === this.selectedCat)
      return cat ? cat.color : '#558b2f'
    },
    /** 正在做模式只选开始时间，隐藏结束时间选择。 */
    showEnd() {
      return this.blockKind !== 'running'
    },
    /** 今天（本地）'yyyy-MM-dd' */
    todayStr() {
      const d = new Date()
      const pad = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    },
    /**
     * 弹窗当前操作的那一天是不是今天。
     *
     * ⚠️ `todayStr` 也是 computed（不是 method），**不能加括号调用**：
     *     `this.todayStr()` 会抛 `TypeError: this.todayStr is not a function`，
     *     而这个 computed 被 `doingDisabled` → 模板依赖链牵着，一抛就是整个弹窗渲染失败
     *     （表现为「点了 + 却完全没有悬浮窗」，控制台才看得到报错）。
     */
    isToday() {
      const day = this.days[this.dayIdx]
      return !!day && String(day.date) === this.todayStr
    },
    /**
     * 「在做」只能从**今天**开始（用户 2026-10-13 定案）：补记昨天、预排明天都不该有「在做」。
     * 编辑已有的块不受限制 —— 尤其是昨天开始、今天才结束的跨天块，收尾时必须能进弹窗改。
     */
    doingDisabled() {
      if (this.eventData) return false
      return !this.isToday
    },
    /** 「时间」一行的灰色说明：在做只提开始时间，记录才提开始 + 结束。 */
    timeHint() {
      if (!this.showEnd) return '（填这件事几点开始，默认当前时间）'
      return this.mode === 'slider' ? '（拖动滑块选开始和结束时间）' : '（选开始和结束时间）'
    },
    /** 提醒时刻（当天分钟坐标），没开定时为 null */
    timerMinute() {
      if (!this.timerEnabled || !this.timerTimeStr) return null
      return this.timeToMinutes(this.timerTimeStr)
    },
    /** 定时时刻的下界：必须晚于开始时间，否则「还没开始就该结束了」 */
    timerFloorMinute() {
      return Math.min(this.selectionStart + 1, DAY_MINUTES - 1)
    },
    /**
     * 提醒时刻的可选范围：**必须晚于开始时间**，否则"还没开始就该结束了"。
     * 上界放到当天 23:59 —— 「在做」本来就是当天的块。
     */
    timerPickerOptions() {
      return { selectableRange: `${this.buildTime(this.timerFloorMinute)}:00 - 23:59:59` }
    },
    /**
     * 类型 → 一套 CSS 变量（选中书签的淡色底 + 强调色），挂在 `.modal-card` 上。
     * ⚠️ 2026-10-13 用户最终定案：**整窗（卡片 / 子页面）恒为纯白，不再随类型变色**，
     *    只有被选中的那个书签带类型色，所以这里不再给卡片/子页面涂色。
     */
    cardKindClass() {
      return {
        done: 'kind--record',
        running: 'kind--doing',
        planned: 'kind--plan'
      }[this.blockKind] || 'kind--record'
    },
    subPageTitle() {
      return {
        done: '记录',
        running: '在做',
        planned: '计划'
      }[this.blockKind] || '记录'
    },
    subPageDesc() {
      if (this.blockKind === 'running') {
        return '正在进行的事：只填开始时间，可加定时提醒。'
      }
      if (this.blockKind === 'planned') {
        return '计划要做的：先填计划时间，做完变成记录。'
      }
      return '已经做完的事：填完整的开始和结束时间。'
    },
    timelineMarks() {
      const marks = {}
      for (let m = 0; m <= DAY_MINUTES; m += 240) {
        if (m >= this.sliderMin && m <= this.sliderMax) {
          marks[m] = this.buildTime(m)
        }
      }
      marks[this.sliderMin] = this.buildTime(this.sliderMin)
      marks[this.sliderMax] = this.buildTime(this.sliderMax)
      return marks
    },
    /**
     * 开始时间的可选下界。
     *
     * ⚠️ 「在做」**刻意放开成 0（整天可选）**，不锁在点击的那个空档开头：
     *     在做只让用户填「开始时间」，结束点是系统按「填充到下一个时间块」推出来的，
     *     所以它天然允许开始时间落在更早的位置 —— 例如 14:00-15:00 已经记了午饭，
     *     但自己其实 14:30 就在做这件事，开始时间就该能选 14:30。
     *     锁死成空档开头会让这种合理输入**根本选不了**。
     *
     *     放开后一旦和已有块重叠，由 `TimeTable.onModalConfirm` 走**同一套**冲突提示
     *     （缩短旧任务 / 调整新任务）裁决，不会静默把旧记录盖掉。
     */
    sliderMin() {
      return this.blockKind === 'running' ? 0 : Math.max(0, this.rangeStart)
    },
    sliderMax() {
      return Math.min(DAY_MINUTES, this.rangeEnd)
    },
    startPercent() {
      const span = this.sliderMax - this.sliderMin
      if (span <= 0) return '0%'
      return `${((this.selectionStart - this.sliderMin) / span) * 100}%`
    },
    endPercent() {
      const span = this.sliderMax - this.sliderMin
      if (span <= 0) return '0%'
      return `${((this.selectionEnd - this.sliderMin) / span) * 100}%`
    },
    startLabelStyle() {
      return { left: this.startPercent }
    },
    endLabelStyle() {
      const span = this.sliderMax - this.sliderMin
      if (span <= 0) return { left: this.endPercent }
      const startP = ((this.selectionStart - this.sliderMin) / span) * 100
      const endP = ((this.selectionEnd - this.sliderMin) / span) * 100
      // 标签带 −/+/时间按钮后整体更宽，最小间距拉到 22% 避免两头挤在一起重叠
      const minGap = 22
      let left = endP
      if (endP > startP && endP - startP < minGap) {
        left = Math.min(100, startP + minGap)
      }
      return { left: `${left}%` }
    }
  },
  watch: {
    /**
     * 切到「在做」：结束时间立刻钉到当前空档末尾（下一个时间块的前一分钟 / 当天 24:00）。
     * 用户不再选结束时间，但块要有终点才知道「填充到哪里」。
     */
    blockKind(val) {
      if (val !== 'running') return
      this.dragEndMinute = this.sliderMax
      // 「在做」= 现在正在做，开始时间默认取「此刻」。
      // 若此刻不在这个空档里（比如点的是早上 8-10 点那段、现在是下午），
      // 就退回空档开头，别让开始时间跑出空档、把块填到不该填的地方。
      //
      // ⚠️ 这里刻意用「空档开头」而不是 `sliderMin`（在做已放开成 0）：
      //    **默认值**要保守 —— 一打开就给一个撞上旧记录的时间，等于逼用户先处理冲突。
      //    想选更早的时间（含落在旧块里）由用户**手动**改，那才是需要弹冲突提示的场景。
      if (!this.eventData) {
        const now = new Date()
        const nowMin = now.getHours() * 60 + now.getMinutes()
        const floor = Math.max(0, this.rangeStart)
        this.dragStartMinute = (nowMin > floor && nowMin < this.sliderMax)
          ? nowMin
          : floor
      }
      if (this.dragStartMinute >= this.dragEndMinute) {
        this.dragStartMinute = Math.max(this.sliderMin, this.dragEndMinute - 1)
      }
      // 定时默认关，预填「开始 + 30 分钟」
      this.timerEnabled = false
      this.timerQuickActive = null
      this.timerTimeStr = this.buildTime(Math.min(this.dragStartMinute + 30, DAY_MINUTES - 1))
    },
    /**
     * 开始时间一变，定时必须跟着挪：提醒时刻不能早于开始时间。
     * 放任不管的话，定时面板的 selectableRange 会把旧值静默夹到下界，
     * 输入框却还显示旧值 —— 又是「选中的时间和滚轴不一致」那一类问题。
     */
    selectionStart(val) {
      if (this.blockKind !== 'running' || !this.timerEnabled) return
      if (this.timerMinute !== null && this.timerMinute < Math.min(val + 1, DAY_MINUTES - 1)) {
        this.applyQuickTimer(this.timerQuickActive || 30)
      }
    },
    visible(val) {
      if (val) {
        this.loadTags().then(() => {
          this.resetFields()
        })
      }
    },
    mode(val) {
      if (val === 'picker') {
        const maxForPicker = Math.min(this.rangeEnd, DAY_MINUTES - 1)
        this.dragStartMinute = Math.min(this.dragStartMinute, maxForPicker)
        this.dragEndMinute = Math.min(this.dragEndMinute, maxForPicker)
        if (this.dragStartMinute === this.dragEndMinute) {
          this.dragEndMinute = Math.min(this.dragStartMinute + 1, maxForPicker)
        }
      }
    }
  },
  methods: {
    async loadTags() {
      if (!this.projectId) {
        this.catOptions = []
        return
      }
      this.loadingCats = true
      try {
        // 好友协同编辑场景：分类走好友接口（自己的 listTagsByProject 查的是当前用户自己的表，
        // 查好友的表会因归属校验失败返回空）。
        const res = this.friendOwnerId
          ? await listFriendTags(this.friendOwnerId, this.projectId)
          : await listTagsByProject(this.projectId)
        if (!res.success || res.code !== 200) {
          console.warn('加载分类失败', res.message)
          this.catOptions = []
          return
        }
        this.catOptions = (res.data || []).map(tag => ({
          key: String(tag.id),
          label: tag.name,
          color: tag.color || '#a0aec0'
        }))
      } catch (e) {
        console.error('加载分类失败', e)
        this.catOptions = []
      } finally {
        this.loadingCats = false
      }
    },
    resetFields() {
      this.blockKind = this.eventData ? (this.eventData.kind || 'done') : 'done'
      if (this.eventData) {
        const found = this.catOptions.find(c => c.key === String(this.eventData.cat))
        this.selectedCat = found ? String(this.eventData.cat) : this.firstCatKey()
        const s = this.eventData.start || 0
        const e = Math.min(
          this.eventData.end || Math.min(s + 60, DAY_MINUTES),
          DAY_MINUTES
        )
        this.dragStartMinute = s
        this.dragEndMinute = e
        this.text = this.eventData.text || ''
      } else {
        this.selectedCat = this.normalizeLastCategory()
        let s = this.rangeStart
        // 正在做：默认开始时间取「当前时刻」，贴合"现在就开始做"的语义。
        // ⚠️ 下界同样用「空档开头」而非 sliderMin（在做已放开成 0）：默认值保守，
        //    落在空档里最符合预期；要更早（含落在旧块里）由用户手动改，那时才弹冲突提示。
        if (this.blockKind === 'running') {
          const now = new Date()
          const nowMin = now.getHours() * 60 + now.getMinutes()
          const floor = Math.max(0, this.rangeStart)
          s = Math.min(Math.max(nowMin, floor), Math.max(floor, this.sliderMax - 1))
        }
        // 在做：结束点 = 当前空档末尾（下一个时间块的前一分钟；后面没块就是 24:00），
        // 界面上不暴露给用户，只作为「填充到哪儿」的边界
        const e = this.blockKind === 'running'
          ? Math.min(this.rangeEnd, DAY_MINUTES)
          : Math.min(this.rangeStart + 150, this.rangeEnd)
        this.dragStartMinute = s
        this.dragEndMinute = e
        this.text = ''
      }
      // 编辑已存在的「在做」块：把它当前挂着的定时**回填**到开关上。
      // 不回填的话，用户只是来改个备注/时间，保存时定时就被当成"没开"静默关掉了。
      const existingTimer = (this.eventData && this.eventData.kind === 'running' && this.eventData.id)
        ? timerOf(String(this.eventData.id))
        : null
      if (existingTimer) {
        this.timerEnabled = true
        this.timerQuickActive = null
        // minute 是当天分钟坐标（scheduleTimer 时写进去的）；老数据没有就退回 label 文案
        this.timerTimeStr = Number.isFinite(existingTimer.minute)
          ? this.buildTime(existingTimer.minute)
          : (existingTimer.label || this.buildTime(Math.min(this.dragStartMinute + 30, DAY_MINUTES - 1)))
      } else {
        this.timerEnabled = false
        this.timerQuickActive = null
        this.timerTimeStr = this.buildTime(Math.min(this.dragStartMinute + 30, DAY_MINUTES - 1))
      }
    },
    firstCatKey() {
      return this.catOptions.length ? this.catOptions[0].key : ''
    },
    /**
     * 供父组件在弹窗**已经打开**的情况下直接改写开始/结束时间。
     *
     * <p>典型场景：时间冲突弹窗里选了「调整新任务」，父组件算出一段不冲突的时间后，
     * 需要把弹窗里的起止时间改过去。此时弹窗没关闭，watch(visible) 不会触发、
     * resetFields() 也不会重跑，只能由父组件主动调这个方法。</p>
     *
     * ⚠️ 时间要夹在滑块的 [sliderMin, sliderMax]（即 rangeStart/rangeEnd）里，
     *    否则 el-slider 会把超界的值拉回边界，出现"改了又弹回去"的假象。
     *    父组件若要放宽范围，应先改 rangeStart/rangeEnd，等 nextTick 后再调本方法。
     */
    applyTimeRange(start, end) {
      const s = this.clampMinute(start)
      const e = this.clampMinute(end)
      this.dragStartMinute = Math.min(s, e)
      this.dragEndMinute = Math.max(s, e)
    },
    clampMinute(value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return this.sliderMin
      return Math.min(Math.max(Math.round(n), this.sliderMin), this.sliderMax)
    },
    normalizeLastCategory() {
      const keys = this.catOptions.map(c => c.key)
      return keys.includes(String(this.lastCategory)) ? String(this.lastCategory) : this.firstCatKey()
    },
    buildTime(minutes) {
      if (minutes >= DAY_MINUTES) return '24:00'
      const total = Math.min(Math.max(minutes, 0), DAY_MINUTES - 1)
      const h = Math.floor(total / 60).toString().padStart(2, '0')
      const m = (total % 60).toString().padStart(2, '0')
      return `${h}:${m}`
    },
    timeToMinutes(timeStr) {
      const [h, m] = timeStr.split(':').map(Number)
      return h * 60 + m
    },
    formatTooltip(val) {
      return this.buildTime(val)
    },
    bumpStart(delta) {
      const next = Math.min(
        Math.max(this.selectionStart + delta, this.sliderMin),
        this.selectionEnd
      )
      this.dragStartMinute = next
      this.dragEndMinute = this.selectionEnd
    },
    bumpEnd(delta) {
      const next = Math.min(
        Math.max(this.selectionEnd + delta, this.selectionStart),
        this.sliderMax
      )
      this.dragStartMinute = this.selectionStart
      this.dragEndMinute = next
    },
    /**
     * 鼠标进入时间标签（数字或微调按钮）→ 立刻显示该端的微调按钮。
     * @param {'start'|'end'} which 控制开始端还是结束端
     */
    enterTune(which) {
      clearTimeout(this.tuneTimer[which])
      this.tuneShow[which] = true
    },
    /**
     * 鼠标离开时间标签。
     * - 离开「数字」(from='text')：延迟 1.6s 再隐藏，留出充足时间把光标从时间移到按钮上点击
     *   （标签内 gap/padding 已收紧，但鼠标慢速移动时仍可能不够，原 0.8s 太短）。
     * - 离开「按钮」(from='btn')：立刻隐藏，不留残影。
     * 二者区分是因为纯 CSS 的 :hover 在离开后同时失效，无法分别控制延迟。
     */
    leaveTune(which, from) {
      clearTimeout(this.tuneTimer[which])
      if (from === 'btn') {
        this.tuneShow[which] = false
      } else {
        this.tuneTimer[which] = setTimeout(() => {
          this.tuneShow[which] = false
        }, 1600)
      }
    },
    toggleMode() {
      this.mode = this.mode === 'slider' ? 'picker' : 'slider'
    },
    /**
     * 打开 / 关闭「定时提醒」。
     * ⚠️ 申请桌面通知权限**必须**在用户手势里做（就是这次点击），
     *    放到别处（页面加载、保存时）浏览器会直接拒绝，之后再也收不到系统提醒。
     */
    toggleTimer() {
      this.timerEnabled = !this.timerEnabled
      if (this.timerEnabled) {
        ensureNotifyPermission()
        // 已有提醒时刻但已被开始时间甩在后面（用户回头改了开始时间）→ 重新预填
        if (!this.timerTimeStr || this.timerMinute < this.timerFloorMinute) {
          this.applyQuickTimer(30)
        }
      }
    },
    /** 快捷时长：按「开始时间 + m 分钟」填提醒时刻（不超出当天 23:59） */
    applyQuickTimer(m) {
      this.timerQuickActive = m
      this.timerTimeStr = this.buildTime(Math.min(this.selectionStart + m, DAY_MINUTES - 1))
    },
    async confirm() {
      if (this.saving) return
      // 定时校验：提醒时刻必须晚于「此刻」，否则一保存就立刻响，没有任何意义
      if (this.timerEnabled) {
        const now = new Date()
        const nowMin = now.getHours() * 60 + now.getMinutes()
        if (this.timerMinute == null || this.timerMinute <= nowMin) {
          this.$message.warning('提醒时间需要晚于当前时间')
          return
        }
      }
      let s = this.selectionStart
      let e = this.selectionEnd
      // ⚠️ 「在做」**不能**把开始时间夹回空档开头：
      //    上面 sliderMin 已经刻意放开成整天，用户选的开始时间可能就落在更早的已有块里
      //    （"我其实 14:30 就在做，可 14:00-15:00 已经记了午饭"）。
      //    这里再夹一次 = 把用户刚选的时间悄悄改掉，冲突提示也就永远弹不出来了。
      //    正确做法是原样送出去，让 TimeTable.onModalConfirm 判冲突后由用户裁决。
      if (this.blockKind !== 'running') {
        s = Math.max(s, this.rangeStart)
      }
      e = Math.min(e, this.rangeEnd)
      // 正在做：没有结束时间 —— 终点取「当前空档末尾」= 下一个时间块的前一分钟
      // （后面没有块时就是当天 24:00）。这样块从开始一路填充到下一个时间块，
      // 也正好对上用户要的「开始到下一个时间块都填充」的效果。
      // 开始时间若落在更早的已有块里，这一段就会和那个块重叠 —— 由冲突提示裁决。
      if (this.blockKind === 'running') {
        e = Math.min(this.rangeEnd, DAY_MINUTES)
      }
      if (s === e) {
        if (s >= this.rangeEnd) {
          s = Math.max(this.rangeEnd - 1, this.rangeStart)
          e = this.rangeEnd
        } else {
          e = Math.min(s + 1, this.rangeEnd)
        }
      }
      if (s > e) {
        const tmp = s
        s = e
        e = tmp
      }
      // ⚠️ 四个原始时间戳必须一起给出去：父组件本地新增的块要靠它们重新派生 start/end/kind。
      //    少了 actualStartTime，之后点「结束」时 onEndRunning 无法用开始日期拼出结束时刻，
      //    本地派生会把块算成 0-0（渲染上整块消失）。
      const plannedStartTime = this.buildDateTime(this.dayIdx, s)
      const plannedEndTime = this.buildDateTime(this.dayIdx, e)
      const actual = { done: [plannedStartTime, plannedEndTime], running: [plannedStartTime, null], planned: [null, null] }[this.blockKind] || [plannedStartTime, plannedEndTime]
      const payload = {
        dayIdx: this.dayIdx,
        start: s,
        end: e,
        cat: this.selectedCat,
        text: this.text,
        kind: this.blockKind,
        plannedStartTime,
        plannedEndTime,
        actualStartTime: actual[0],
        actualEndTime: actual[1],
        // 定时提醒：只有在做 + 开了定时才有值（当天分钟坐标），由父组件换算成绝对时刻后交给全局定时器
        timerAt: this.blockKind === 'running' && this.timerEnabled ? this.timerMinute : null
      }
      this.saving = true
      try {
        if (this.eventData) {
          payload.index = this.eventData.index
          payload.key = this.eventData.key
          // 好友协同编辑场景：落库交给父组件走好友接口，这里不调自己的时间轴接口
          // （调了会因无权限报错，且会和父组件好友接口重复）。
          if (!this.friendOwnerId) {
            await this.updateRecord(s, e, this.blockKind)
          }
        } else {
          // 同样，好友场景只 emit，不本地落库。
          if (!this.friendOwnerId) {
            payload.id = await this.createRecord(s, e, this.blockKind)
          }
        }
        this.$emit('confirm', payload)
      } catch (e) {
        console.error('保存时间记录失败', e)
        this.$message.error('保存失败，请稍后重试')
      } finally {
        this.saving = false
      }
    },
    async createRecord(startMinutes, endMinutes, kind) {
      const workspaceId = localStorage.getItem('taskblock-current-workspace-id')
      if (!workspaceId || !this.projectId) {
        return null
      }
      const startTime = this.buildDateTime(this.dayIdx, startMinutes)
      const endTime = this.buildDateTime(this.dayIdx, endMinutes)
      const tag = this.catOptions.find(c => String(c.key) === String(this.selectedCat))
      // 三种类型 → 不同的 planned/actual 取值 + 后端执行状态
      const params = {
        workspaceId,
        projectId: this.projectId,
        tagId: tag ? tag.key : null,
        blockType: 'RECORD',
        plannedStartTime: startTime,
        plannedEndTime: endTime,
        content: this.text || '',
        color: tag ? tag.color : null
      }
      if (kind === 'running') {
        // 在做：只知道开始时间，结束未知 → actualEnd 留空，状态 RUNNING
        params.executionStatus = 'RUNNING'
        params.actualStartTime = startTime
        params.actualEndTime = null
      } else if (kind === 'planned') {
        // 计划：还没做 → actual 都为空，状态 PLANNED
        params.executionStatus = 'PLANNED'
        params.actualStartTime = null
        params.actualEndTime = null
      } else {
        // 记录：已开始且已结束 → actual = planned，状态 COMPLETED
        params.executionStatus = 'COMPLETED'
        params.actualStartTime = startTime
        params.actualEndTime = endTime
      }
      const res = await createTimeBlock(params)
      if (!res.success || res.code !== 200) {
        throw new Error(res.message || '创建时间记录失败')
      }
      return res.data && res.data.id ? String(res.data.id) : null
    },
    async updateRecord(startMinutes, endMinutes, kind) {
      if (!this.eventData || !this.eventData.id) {
        return
      }
      const startTime = this.buildDateTime(this.dayIdx, startMinutes)
      const endTime = this.buildDateTime(this.dayIdx, endMinutes)
      const tag = this.catOptions.find(c => String(c.key) === String(this.selectedCat))
      // actual 取值随类型变化；状态流转（RUNNING/COMPLETED）由专用接口负责，
      // 这里只保证时间字段正确（后端 update 不改 executionStatus）。
      const actual = { done: [startTime, endTime], running: [startTime, null], planned: [null, null] }[kind || 'done']
      try {
        const res = await updateTimeBlock(this.eventData.id, {
          projectId: this.projectId,
          tagId: tag ? tag.key : null,
          plannedStartTime: startTime,
          plannedEndTime: endTime,
          actualStartTime: actual[0],
          actualEndTime: actual[1],
          content: this.text || '',
          color: tag ? tag.color : null,
          note: ''
        })
        if (!res.success || res.code !== 200) {
          // ⚠️ 必须抛出去：让 confirm() 中断、不再 emit，否则界面显示新数据、
          //    后端还是旧数据，刷新一下就"改了又变回去"
          throw new Error(res.message || '修改时间记录失败')
        }
      } catch (e) {
        console.error('修改时间记录失败', e)
        throw e
      }
    },
    buildDateTime(dayIdx, minutes) {
      const day = this.days[dayIdx]
      if (!day) return null
      const [y, M, d] = day.date.split('-').map(Number)
      const date = new Date(y, M - 1, d)
      date.setMinutes(date.getMinutes() + minutes)
      const yy = date.getFullYear()
      const MM = String(date.getMonth() + 1).padStart(2, '0')
      const dd = String(date.getDate()).padStart(2, '0')
      const hh = String(date.getHours()).padStart(2, '0')
      const mm = String(date.getMinutes()).padStart(2, '0')
      const ss = String(date.getSeconds()).padStart(2, '0')
      return `${yy}-${MM}-${dd}T${hh}:${mm}:${ss}`
    },
    cancel() {
      this.$emit('cancel')
    },
    /**
     * 「修改记录」弹窗里的「删除」：交给父组件（TimeTable.onModalDelete）走删除流程
     * （二次确认 + 调接口 + emit delete-event），与右上角 × 的删除路径完全一致。
     */
    remove() {
      this.$emit('delete')
    }
  }
}
</script>

<style scoped>
.modal-overlay{
  position:fixed;
  top:0; left:0; right:0; bottom:0;
  background:rgba(45,55,72,0.45);
  display:flex;
  align-items:center;
  justify-content:center;
  z-index:1000;
  padding:1rem;
  font-family:'DynaPuff','OPPOSans',Avenir,Helvetica,Arial,sans-serif;
}
.modal-card{
  position:relative;
  width:520px;
  max-width:calc(100vw - 32px);
  max-height:calc(100vh - 32px);
  overflow:auto;
  background:#fff;
  border-radius:0.75rem;
  border:1px solid #ebeef5;
  padding:1.875rem;
  box-shadow:0 20px 50px rgba(0,0,0,0.15);
  font-family:'DynaPuff','OPPOSans',Avenir,Helvetica,Arial,sans-serif;
}
.modal-card >>> .el-input__inner,
.modal-card >>> .el-textarea__inner,
.modal-card >>> .el-button,
.modal-card >>> .el-radio-button__inner{
  font-family:'DynaPuff','OPPOSans',Avenir,Helvetica,Arial,sans-serif;
}
/* 三种类型各一套：选中书签的淡色底 + 强调色（字色）。
   整窗（卡片 / 子页面）一律纯白、不再随类型变色 —— 用户 2026-10-13 最终定案。
   变量挂在 `.modal-card` 上（书签行已移回卡片内部，不再需要外层 shell） */
.modal-card.kind--record{
  --bm-tab-bg:#daf0e2;
  --bm-accent:#2f7a4a;
}
.modal-card.kind--doing{
  --bm-tab-bg:#dce9ff;
  --bm-accent:#1d4ed8;
}
.modal-card.kind--plan{
  --bm-tab-bg:#eae0ff;
  --bm-accent:#6d28d9;
}
/* 书签行：**卡片内部**的第一行，标题(左) + 记录/在做/计划 + ×(右)，与下面内容同属一张卡、一个整体。
   不再单独铺白底 / 描边 / 投影——那些东西会让它看起来是被切开的两块 */
.bookmark-row{
  display:flex;
  align-items:flex-end;
  gap:0.5rem;
  margin-bottom:0.875rem;
}
.bookmark-group{
  display:flex;
  align-items:flex-end;
  gap:0.25rem;
  margin-left:auto;
}
/* 书签按钮：默认白底（与白底行融为一体，只靠字色区分）；hover 才浮出一点灰 */
.bookmark{
  position:relative;
  padding:0.25rem 0.8rem 0.35rem;
  font-family:inherit;
  font-size:0.875rem;
  line-height:1.2;
  color:#718096;
  background:#fff;
  border:none;
  border-radius:0.5rem 0.5rem 0 0;
  cursor:pointer;
  transition:color .18s ease, background .18s ease, padding-top .18s ease;
}
.bookmark:hover{
  color:#2d3748;
  background:#f1f5f9;
}
.bm-title{
  font-size:1rem;
  font-weight:600;
  letter-spacing:0.02em;
  color:#2d3748;
  cursor:default;
}
.bm--close{
  margin-left:0.375rem;
  padding:0.25rem 0.6rem 0.35rem;
  font-size:1.25rem;
  line-height:1;
}
.bm--close:hover{
  color:#e53e3e;
}
/* 选中：淡色底 + 强调色字，整枚往上顶（padding-top 加大，配合 align-items:flex-end 往上长）。
   注意：**没有**底部横线，用户明确要求去掉 */
.bookmark.on{
  padding-top:0.6rem;
  font-weight:600;
  color:var(--bm-accent);
  background:var(--bm-tab-bg);
}
/* 「在做」在非当天禁用：置灰 + 禁止手型，title 提示「只能从今天开始」。
   只改观感，不动尺寸/结构（用户 2026-10-13 定案：在做只能选当天）。 */
.bookmark.is-off,
.bookmark:disabled{
  color:#c3cbd4;
  cursor:not-allowed;
  background:#fff;
}
.bookmark.is-off:hover,
.bookmark:disabled:hover{
  color:#c3cbd4;
  background:#fff;
}
.form-item{
  margin-bottom:1.563rem;
}
/* 缩小「事项」区域与「取消 / 保存」按钮之间的间距：
   去掉最后一个 form-item 的下边距，页脚下边距也压到 0.875rem */
.modal-card .form-item:last-child{
  margin-bottom:0;
}
.modal-card .footer{
  margin-top:0.875rem;
}
.form-item label{
  display:flex;
  align-items:center;
  margin-bottom:0.625rem;
  color:#666;
  font-size:0.875rem;
}
.label-note{
  margin-left:0.25rem;
  color:#a0aec0;
  font-size:0.75rem;
  font-weight:normal;
}
.custom-switch{
  margin-left:auto;
  cursor:pointer;
  user-select:none;
}
.switch-track{
  position:relative;
  width:60px;
  height:28px;
  border-radius:14px;
  background:#67c23a;
  overflow:hidden;
  box-shadow:inset 0 1px 3px rgba(0,0,0,0.12);
  transition:background .2s;
}
.custom-switch.blue .switch-track{
  background:#409eff;
}
.switch-thumb{
  position:absolute;
  top:2px;
  left:2px;
  width:24px;
  height:24px;
  border-radius:50%;
  background:#fff;
  box-shadow:0 2px 4px rgba(0,0,0,0.2);
  transition:left .2s;
}
.switch-thumb.right{
  left:34px;
}
.cat-loading,
.cat-empty{
  font-size:0.813rem;
  color:#718096;
  padding:0.625rem 0;
}
.cat-buttons{
  display:flex;
  flex-wrap:wrap;
  gap:0.5rem;
}
.cat-buttons >>> .el-button:hover{
  opacity:0.85;
}
/* 子页面：横向充满整个悬浮窗。**不再随类型变色**（用户 2026-10-13 定案：整窗纯白），
   所以这里没有任何 background，也删掉了原来的 .sub-page--record/doing/plan 三条配色 */
.sub-page{
  border-radius:0;
  padding:1.25rem 1.875rem;
  margin:0 -1.875rem;
}
.sub-page-head{
  display:flex;
  align-items:baseline;
  flex-wrap:wrap;
  gap:0.5rem;
  margin-bottom:1.25rem;
}
.sub-page-title{
  font-size:1rem;
  font-weight:600;
  color:#2d3748;
}
.sub-page-desc{
  font-size:0.75rem;
  color:#a0aec0;
  line-height:1.4;
}
.time-row{
  display:flex;
  gap:0.938rem;
}
.time-row >>> .el-date-editor{
  flex:1;
}
/* ===== 「定时」板块（仅「在做」）=====
   开关在 label 行右侧，打开后在下面展开「提醒时间选择器 + 一排快捷时长」。
   只动配色/间距/圆角，结构尺寸跟随 .time-row 与 .custom-switch 已有的那套。 */
.timer-body{
  margin-top:0.125rem;
}
.timer-quick{
  display:flex;
  flex-wrap:wrap;
  align-items:center;
  gap:0.375rem;
  margin-top:0.625rem;
}
.timer-quick-btn{
  padding:0.25rem 0.625rem;
  font-family:inherit;
  font-size:0.75rem;
  line-height:1.4;
  color:#4a5568;
  background:#fff;
  border:1px solid #e2e8f0;
  border-radius:0.75rem;
  cursor:pointer;
  transition:background .15s, color .15s, border-color .15s;
}
.timer-quick-btn:hover{
  border-color:#67c23a;
  color:#2f7a4a;
}
.timer-quick-btn.on{
  background:#2f7a4a;
  border-color:#2f7a4a;
  color:#fff;
  font-weight:600;
}
.timeline-slider{
  padding:0 0.75rem;
  --slider-color:#558b2f;
}
.slider-labels-top{
  position:relative;
  /* 标签内含 −/+ 按钮，整体比纯文字高，给足高度避免被裁切 */
  height:1.75rem;
  margin-bottom:0.25rem;
}
.slider-marks-spacer{
  height:1.25rem;
  margin-top:0.25rem;
}
/* 时间轴顶部的时间标签：绝对定位、自身水平居中（translateX(-50%)），
   内部横排 [−] [时间] [+]，左右各一个微调按钮直接加减该端时间，不再单列微调行。
   gap/padding 都刻意压小：让 ± 按钮尽量贴近时间，鼠标在时间与按钮之间穿梭时
   hover 不容易掉；左右 padding 让 hover 区比视觉更宽一些，给鼠标留点余量。 */
.thumb-label{
  position:absolute;
  top:0;
  transform:translateX(-50%);
  display:flex;
  align-items:center;
  gap:0.05rem;
  padding:0.125rem 0.25rem;
  transition:left .1s;
}
.lbl-text{
  font-size:0.75rem;
  font-weight:600;
  color:#558b2f;
  white-space:nowrap;
  min-width:2.25rem;
  text-align:center;
  font-variant-numeric:tabular-nums;
  cursor:default;
}
/* 时间标签旁的 −/+ 微调按钮：默认隐藏，由 JS 切换 .show-tune 控制显隐
   （区分「离开数字延迟隐藏」与「离开按钮立即隐藏」两种行为，纯 CSS 做不到） */
.lbl-btn{
  width:1.125rem;
  height:1.125rem;
  display:flex;
  align-items:center;
  justify-content:center;
  border:none;
  background:transparent;
  color:#4a5568;
  font-size:0.875rem;
  line-height:1;
  padding:0;
  cursor:pointer;
  user-select:none;
  opacity:0;
  pointer-events:none;
  transition:opacity .15s ease, background .12s, color .12s, transform .08s;
}
.thumb-label.show-tune .lbl-btn{
  opacity:1;
  pointer-events:auto;
}
.lbl-btn:hover:not(:disabled){
  background:#edf2f7;
  color:#2d3748;
}
.timeline-slider >>> .el-slider__bar{
  background-color:var(--slider-color);
}
.timeline-slider >>> .el-slider__button{
  border-color:var(--slider-color);
}
.timeline-slider >>> .el-slider__button:hover,
.timeline-slider >>> .el-slider__button.dragging{
  background-color:var(--slider-color);
  border-color:var(--slider-color);
}
.timeline-slider >>> .el-slider__stop{
  width:2px;
  height:6px;
  border-radius:0;
  background-color:#e2e8f0;
  transform:translateX(-50%);
}
.footer{
  text-align:right;
  margin-top:1.875rem;
}

.modal-fade-enter-active, .modal-fade-leave-active{
  transition:opacity .2s;
}
.modal-fade-enter, .modal-fade-leave-to{
  opacity:0;
}

/* 2026-10-13 用户要求：**只有「在做」的「定时」开关**才是开关语义 —— 关闭 = 灰、开启 = 绿。
   原 `.switch-track` 的默认态写死成 `#67c23a`（Element 成功绿），导致定时没开也发绿。
   这里只给 `.timer-switch` 加默认灰，**不碰 `.switch-track` 本身**：
   「记录 / 计划」里的「滑块/选择器」按钮表示的是**模式切换**（不是开关），
   沿用原有配色：默认绿、选中（.custom-switch.blue）蓝，一律保持原样。 */
.timer-switch .switch-track{
  background:#c0c4cc;
}
.timer-switch.on .switch-track{
  background:#67c23a;
}
</style>

<style>
/* 时间选择器下拉面板挂载在 body 下，需全局覆盖字体 */
.quick-add-time-picker{
  font-family:'DynaPuff','OPPOSans',Avenir,Helvetica,Arial,sans-serif;
}
/* ⛔ 绝对不要改 `.el-time-spinner__list::before/::after` 的 height（Element 原值 80px）：
   Element 的 `adjustSpinner` 是 `scrollTop = value * 32px` 硬算的，没有把占位块算进去，
   靠的就是这 80px（=2.5 行）把加粗项推到滚轮可视区中线。改成 16px 会让加粗项上移
   64px（正好 2 行），症状就是「输入框 20:15、滚轮中线却是 22:17」（Chrome 实测 31px vs 80px）。
   嫌顶部空白多也只能改 `.el-time-spinner__wrapper` 的 max-height，不能动这两个占位块。 */
</style>
